import { useURLSearchParams } from '@hooks/useURLSearchParams';
import { Box, Stack, styled } from '@mui/material';
import { LadderSettings } from '@protos/ladders';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { useUserProductsAndTenorsContext } from '@shared/contexts/UserProductsAndTenorsProvider';
import { filterColumnChooserFromMainMenu } from '@utils/contextMenuUtils';
import { AG_GRID_LICENCE_KEY } from '@utils/grid';
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  GridApi,
  GridReadyEvent,
  LicenseManager,
  NavigateToNextCellParams,
  ValueFormatterParams,
  ValueParserParams,
} from 'ag-grid-enterprise';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-balham.css';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OrderInputCellEditor } from '../OrderInputCellEditor';
import { OrderInputCellRenderer } from '../OrderInputCellRenderer';
import { SpreadSettings } from './SpreadSettings';
import { useTradableProductSymbolsContext } from '@shared/contexts/TradableProductSymbolsProvider';

const NO_CONTENT_CELL_CLASS = 'no-content';

const TENOR_COLUMN: ColDef<any> = {
  headerName: '',
  field: 'name',
  width: 100,
  sortable: false,
  pinned: 'left',
  lockPosition: 'left',
  cellClass: (params: CellClassParams) => {
    if (params.data.name.includes('JUN') || params.data.name.includes('DEC')) {
      return 'ag-tenor-column june-dec-cell';
    }

    return 'ag-tenor-column';
  },
};

const StyledBox = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  [theme.breakpoints.down('sm')]: {
    width: 'calc(100vw - 26px)',
    height: 'calc(100vh - 250px)',
  },
}));

interface RowData {
  [key: string]: { bid: string; offer: string } | string | number;
}

interface SpreadOCLGridProps {
  getGridApi?: (api: GridApi) => void;
  getSpreadSettings?: (settings: LadderSettings) => void;
  selectedAccount: string;
  savedSettings?: {
    [key: string]: { bid: number | ''; offer: number | '' };
  };
  onUpdateSettings: (symbol: string, tenorKey: string, values: { bid: number | ''; offer: number | '' }) => Promise<void>;
  stopEditing: () => void;
}

LicenseManager.setLicenseKey(AG_GRID_LICENCE_KEY);

const spreadSettingsKey = 'flux-ocl-spread-settings';

const getSavedSettings = () => {
  const saved = localStorage.getItem(spreadSettingsKey);
  if (saved) return JSON.parse(saved);
  return null;
};

export const SpreadOCLGrid = ({
  getGridApi,
  getSpreadSettings,
  selectedAccount,
  savedSettings = {},
  onUpdateSettings,
  stopEditing,
}: SpreadOCLGridProps) => {
  const { tenors } = useUserProductsAndTenorsContext();
  const { urlParams } = useURLSearchParams();
  const gridApiRef = useRef<GridApi | null>(null);
  const user = useUserContext();
  const { allProducts } = useTradableProductSymbolsContext();
  const [spreadSettings, setSpreadSettings] = useState<LadderSettings>({
    symbol: urlParams.spreadSymbol || getSavedSettings()?.symbol || '',
    validity: Number(urlParams.spreadValidity) || getSavedSettings()?.validity || 10,
    size: Number(urlParams.spreadSize) || getSavedSettings()?.size || 10,
    tick_size: 2,
  });

  const first24MonthlyTenors = useMemo(() => tenors.filter(tenor => tenor.frequency === 'monthly').slice(0, 24), [tenors]);
  const allVisibleTenors = useMemo(() => first24MonthlyTenors.slice(1, first24MonthlyTenors.length - 1), [first24MonthlyTenors]);
  const transformedRowData = useMemo(
    () =>
      allVisibleTenors.reduce((acc, baseTenor) => {
        const rowTenor = `${baseTenor.code}`;
        let newElement = {
          id: rowTenor,
          name: baseTenor.display.toLocaleUpperCase(),
          symbol: spreadSettings.symbol,
          validity: spreadSettings.validity,
          size: spreadSettings.size.toString(),
          account_id: selectedAccount,
        };

        allVisibleTenors.forEach(tailTenor => {
          const columnTenor = `${tailTenor.code}`;

          // Use savedSettings from props instead of currentSettings
          const settingsKey = `${spreadSettings.symbol}_${rowTenor}-${columnTenor}`;
          const savedValues = savedSettings[settingsKey] ?? { bid: '', offer: '' };

          newElement = {
            ...newElement,
            [`${columnTenor}`]: {
              bid: savedValues.bid ?? '',
              offer: savedValues.offer ?? '',
            },
          };
        });

        acc.push(newElement);
        return acc;
      }, [] as RowData[]),
    [allVisibleTenors, spreadSettings, selectedAccount, savedSettings]
  );

  const allGridColumns: (ColDef<any, any> | ColGroupDef<any>)[] = useMemo(
    () => [
      TENOR_COLUMN,
      ...first24MonthlyTenors.slice(2, first24MonthlyTenors.length - 1).map((tenor, index) => ({
        headerName: tenor.display.toLocaleUpperCase(),
        field: `${tenor.code}`,
        cellClass: params => {
          const columnIndex = first24MonthlyTenors.findIndex(elem => elem.display.toLocaleUpperCase() === params.colDef.headerName);

          if (
            (params.rowIndex !== 0 && columnIndex <= params.rowIndex) ||
            transformedRowData[params.rowIndex].name === first24MonthlyTenors[columnIndex].display.toLocaleUpperCase()
          ) {
            return NO_CONTENT_CELL_CLASS;
          }

          if (params.data?.name?.includes('JUN') || params.data?.name.includes('DEC')) {
            if (params.colDef?.headerName.includes('JUN') || params.colDef?.headerName.includes('DEC')) {
              return 'june-dec-cell-anamoly';
            }
            return 'june-dec-cell';
          }

          if (params.colDef?.headerName.includes('JUN') || params.colDef?.headerName.includes('DEC')) {
            return 'june-dec-cell-vertical';
          }

          return undefined;
        },
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value) {
            return params.value;
          }
        },
        valueParser: (params: ValueParserParams) => {
          if (params.newValue) {
            return { bid: params.newValue, offer: params.newValue };
          }

          return { bid: null, offer: null };
        },
        cellRenderer: OrderInputCellRenderer,
        cellEditor: OrderInputCellEditor,
        cellEditorParams: {
          settings: spreadSettings,
          selectedAccount,
          onUpdateSettings,
          allProducts,
        },
        cellRendererParams: {
          spreadSettings,
        },
        editable: () => true,
        minWidth: 120,
        maxWidth: 120,
      })),
    ],
    [first24MonthlyTenors, spreadSettings, allProducts]
  );

  const navigateToNextCell = useCallback((params: NavigateToNextCellParams<RowData, any>) => {
    const nextCell = params.nextCellPosition;
    if (nextCell) {
      const cellClass = nextCell.column.getColDef().cellClass;
      const cellClassParams = {
        ...nextCell.column,
        rowIndex: nextCell.rowIndex,
      } as unknown as CellClassParams;

      const isNoContent =
        typeof cellClass === 'string' || Array.isArray(cellClass)
          ? cellClass.includes(NO_CONTENT_CELL_CLASS)
          : cellClass?.(cellClassParams) === NO_CONTENT_CELL_CLASS;

      if (isNoContent) {
        return null;
      }
    }

    return nextCell;
  }, []);

  const onCellFocused = useCallback(event => {
    if (!!event.column && event.rowIndex !== null) {
      const focusedCell = event.api.getFocusedCell();
      const currentlyEditingCell = event.api.getEditingCells()[0];

      const isEditing =
        focusedCell?.rowIndex === currentlyEditingCell?.rowIndex && focusedCell?.column.getColId() === currentlyEditingCell?.column.getColId();

      if (!isEditing) {
        const columnDef = event.column.getColDef();
        if (columnDef?.editable) {
          event.api.startEditingCell({
            rowIndex: event.rowIndex,
            colKey: event.column.getColId(),
          });
        }
      }
    }
  }, []);

  const onGridReady = useCallback((params: GridReadyEvent<RowData, any>) => {
    gridApiRef.current = params.api;
    getGridApi?.(params.api);
  }, []);

  const changeSettings = (newSettings: LadderSettings) => {
    localStorage.setItem(spreadSettingsKey, JSON.stringify(newSettings));
    setSpreadSettings(newSettings);
  };

  useEffect(() => {
    if (!spreadSettings?.symbol || !user?.id) return;

    const debounceTimer = setTimeout(() => {
      getSpreadSettings?.(spreadSettings);
    }, 250);

    return () => clearTimeout(debounceTimer);
  }, [spreadSettings, getSpreadSettings, user?.id]);

  if (!spreadSettings) return null;

  return (
    <Stack display="flex" flex={1} height="100%" gap={1}>
      <SpreadSettings spreadSettings={spreadSettings} setSpreadSettings={changeSettings} stopEditing={stopEditing} />
      <StyledBox className="ag-theme-balham-dark ocl-grid">
        <AgGridReact
          suppressScrollOnNewData
          suppressFocusAfterRefresh
          suppressRowVirtualisation
          autoSizeStrategy={{ type: 'fitCellContents', skipHeader: false }}
          defaultColDef={{
            suppressKeyboardEvent: () => true,
            suppressHeaderMenuButton: true,
          }}
          getMainMenuItems={filterColumnChooserFromMainMenu}
          suppressDragLeaveHidesColumns
          suppressColumnMoveAnimation
          rowData={transformedRowData}
          tooltipShowDelay={0}
          onGridReady={onGridReady}
          onCellFocused={onCellFocused}
          domLayout="autoHeight"
          containerStyle={{ height: '100%', width: '100%' }}
          getRowId={({ data }): string => data?.id?.toString()}
          columnHoverHighlight={false}
          navigateToNextCell={navigateToNextCell}
          columnDefs={allGridColumns}
          rowBuffer={50}
        />
      </StyledBox>
    </Stack>
  );
};
